import React, { useState } from 'react'
import Button from '@mui/material/Button'
import StyledButton from '@/components/Button/Button'
import FilterModal from '@/components/FilterModal'
import Popup from '@/components/Common/Popup'
import TimeClockPicker from '../TimeClockPicker'
import VerificationFilter from '../VerificationFilter'
import usePostAdminImposedCheckIn from '@/services/hooks/usePostAdminImposedCheckIn'
import usePostAdminRandomCheckIn from '@/services/hooks/usePostAdminRandomCheckIn'
import { invalidateQuery } from '@/services/config/queryClient'
import cacheKeys from '@/services/cacheKeys'
import {
  AdminImposedCheckInPayload,
  FetchAdminRandomCheckInPayload,
} from '@/services/types/APIRequestType'
import { TimePeriod } from '@/utils/config/user-enums'
import { getDateRange } from '@/utils'
import alert_icon from '@/assets/RandomCheckIns/alert-icon.svg'
import { AdminRandomCheckInButtonStatusResponse } from '@/services/types/APIResponseType'

interface RandomCheckInSelectorProps {
  activeFilter: TimePeriod
  checkInButtonStatus: AdminRandomCheckInButtonStatusResponse
  onFilterChange: (filter: TimePeriod) => void
  verificationStatus: string | null
  setVerificationStatus: (newStatus: string | null) => void
}

interface SelectedCheckInTimeType {
  initiatedDate: string
  initiatedTime: string
  initiatedExpiryTime: string
}

const RandomCheckInSelector: React.FC<RandomCheckInSelectorProps> = ({
  activeFilter,
  checkInButtonStatus,
  onFilterChange,
  verificationStatus,
  setVerificationStatus,
}) => {
  const [, setErrorMsg] = useState<string>('')
  const [adminRandomCheckInIds, setAdminRandomCheckInIds] = useState<string[]>(
    [],
  )
  const [open, setOpen] = useState<boolean>(false)
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const [selectedCheckInTime, setSelectedCheckInTime] =
    useState<SelectedCheckInTimeType>({
      initiatedDate: '',
      initiatedTime: '',
      initiatedExpiryTime: '',
    })

  const { mutate: getAdminRandomCheckIn } = usePostAdminRandomCheckIn()
  const { mutate: submitAdminCheckIn } = usePostAdminImposedCheckIn()

  const handleDateChange = (
    selectedTime: string,
    expiryTime: string,
    selectedDate: string,
  ) => {
    setSelectedCheckInTime({
      initiatedDate: selectedDate,
      initiatedTime: selectedTime,
      initiatedExpiryTime: expiryTime,
    })
  }

  const handleFilterChange = (filter: TimePeriod) => {
    onFilterChange(filter)
  }

  const handleSetCheckInButton = () => {
    const requestData: FetchAdminRandomCheckInPayload = {
      ...getDateRange(activeFilter),
      authentication: verificationStatus || '',
    }
    setShowPopup(true)
    getAdminRandomCheckIn(
      {
        payload: requestData,
      },
      {
        onSuccess: (response) => {
          if (response) {
            const adminIds = response?.data?.results.map((item) => item._id)
            setAdminRandomCheckInIds(adminIds)
          }
        },
      },
    )
  }

  const handleClickYes = () => {
    const requestData: AdminImposedCheckInPayload = {
      checkIn_initiated_date: selectedCheckInTime.initiatedDate,
      checkIn_initiated_expiry_time: selectedCheckInTime.initiatedExpiryTime,
      checkIn_initiated_time: selectedCheckInTime.initiatedTime,
      participantIds: adminRandomCheckInIds,
      ...(checkInButtonStatus?.impose && {
        imposeId: checkInButtonStatus?.impose?._id,
      }),
      method:
        checkInButtonStatus?.impose !== null
          ? checkInButtonStatus.impose.checkIn_initiated_date ===
            selectedCheckInTime.initiatedDate
            ? 'put'
            : 'post'
          : 'post',
    }

    submitAdminCheckIn(requestData, {
      onSuccess: () => {
        setShowPopup(false)
        invalidateQuery([cacheKeys.ADMIN_RANDOM_CHECK_IN_BUTTON_STATUS])
      },
      onError: (error) => {
        setErrorMsg(error.message || 'Something went wrong. Please try again.')
      },
    })
  }

  return (
    <>
      <div className="rounded-[37px] p-5 xl:p-[40px] h-auto xl:h-[212px] bg-[#606F8C26] relative">
        <div className="flex flex-col gap-6 text-left">
          <div className="font-semibold text-[18px] xl:text-[24px] leading-[24px] xl:leading-[32.68px] tracking-[-0.02em">
            Set Random Check-in Timings
          </div>
          <div className="flex flex-col xl:flex-row gap-4 xl:gap-14 items-stretch xl:items-center">
            <div className="w-full xl:w-[280px] h-[75px] flex items-center">
              <TimeClockPicker
                onDateChange={handleDateChange}
                isCronRan={checkInButtonStatus?.impose?.is_cron_ran || false}
                checkInInitiatedDate={
                  checkInButtonStatus?.impose?.checkIn_initiated_date || ''
                }
                checkInInitiatedTime={
                  checkInButtonStatus?.impose?.checkIn_initiated_time || ''
                }
              />
            </div>
            <div>
              <StyledButton
                className="h-[60px] w-full xl:w-[270px] bg-Purple-700 text-2 font-inter-3"
                onClick={handleSetCheckInButton}
                disabled={
                  !(
                    checkInButtonStatus?.impose === null ||
                    (checkInButtonStatus?.impose &&
                      checkInButtonStatus?.impose?.button === 'enable')
                  )
                }
              >
                <p className="text-xl">Set Random Check-in</p>
              </StyledButton>
            </div>
          </div>
          <div className=" static mt-2 xl:mt-0 xl:absolute xl:top-6 xl:right-8 flex flex-wrap gap-3 justify-end items-center">
            <div>
              <VerificationFilter
                value={verificationStatus}
                onChange={setVerificationStatus}
              />
            </div>
            <div>
              <div className="dashboard-filter">
                <Button onClick={() => setOpen(true)}>
                  <img
                    src="/icon/Filter 1.png"
                    className="h-12 w-12"
                    alt="Date filter icon"
                  />
                </Button>
              </div>
              <FilterModal
                open={open}
                handleClose={() => setOpen(false)}
                activeFilter={activeFilter}
                handleFilterChange={handleFilterChange}
                filterModelStyle="!top-[42%]"
              />
            </div>
          </div>
        </div>
      </div>
      {showPopup && (
        <Popup
          showYesNoButtons={true}
          icon={
            <img className="h-16 w-16 max-w-max" src={alert_icon} alt="alert" />
          }
          onNoClick={() => setShowPopup(false)}
          onYesClick={handleClickYes}
          title={
            <>
              Are You Sure You <br />
              Set a Random Check-in?
            </>
          }
          message="You’re about to set a check-in time. Please confirm if this is correct"
          className="!bg-[#FB5012]"
        />
      )}
    </>
  )
}

export default RandomCheckInSelector
